import { useDynamicYieldApi } from '~/api/use-dynamic-yield-api';
import * as DataDogLogs from '~/lib/datadog/datadog';
import { isDynamicYieldEnabled } from '~/lib/dynamic-yield/dynamic-yield-settings';

// Module-level state (private)
let originalContext = null;
let isContextSwitched = false;

/**
 * Checks if Dynamic Yield is enabled and initialized
 * @returns {boolean} True if DY is ready
 */
const checkDynamicYieldReady = () => {
  if (!isDynamicYieldEnabled()) {
    return false;
  }

  try {
    const { isDynamicYieldInitialized } = useDynamicYieldApi();
    return isDynamicYieldInitialized();
  } catch (error) {
    DataDogLogs.warn('[Allbirds pixel] - Dynamic Yield Context - DY not available', error);
    return false;
  }
};

/**
 * Saves the current recommendationContext and switches to CART context
 *
 * This function is essential for the cart drawer Dynamic Yield integration because:
 * - It preserves the original page context (PDP, homepage, etc.) when the cart drawer opens
 * - Switches to CART context to enable cart-specific Dynamic Yield campaigns and recommendations
 * - Allows proper context restoration when the cart drawer is closed, maintaining seamless user experience
 * - Ensures cart-specific personalization features work correctly while maintaining the underlying page context
 *
 * Without this function, opening the cart drawer would lose the original page context permanently,
 * breaking Dynamic Yield's ability to provide contextually relevant recommendations and campaigns.
 *
 * @param {string[]} cartSkus - Array of SKUs currently in the cart
 * @returns {boolean} True if context was successfully switched
 */
export const switchToCartContext = (cartSkus = []) => {
  if (!checkDynamicYieldReady()) {
    DataDogLogs.warn(
      '[Allbirds pixel] - Dynamic Yield Context - Cannot switch to cart, DY not ready',
    );
    return false;
  }

  try {
    // Save current context if not already saved
    if (!isContextSwitched) {
      originalContext = { ...window.DY.recommendationContext };
      DataDogLogs.info('[Allbirds pixel] - Dynamic Yield Context - Saved original context', {
        originalContext,
      });
    }

    // Switch to cart context
    window.DY.recommendationContext = { data: cartSkus, type: 'CART' };
    isContextSwitched = true;

    DataDogLogs.info('[Allbirds pixel] - Dynamic Yield Context - Switched to CART context', {
      cartSkus,
    });
    return true;
  } catch (error) {
    DataDogLogs.error('[Allbirds pixel] - Dynamic Yield Context - Error switching to cart', error);
    return false;
  }
};

/**
 * Restores the original recommendationContext
 * @returns {boolean} True if context was successfully restored
 */
export const restoreOriginalContext = () => {
  if (!checkDynamicYieldReady()) {
    DataDogLogs.warn('[Allbirds pixel] - Dynamic Yield Context - Cannot restore, DY not ready');
    return false;
  }

  if (!isContextSwitched || !originalContext) {
    DataDogLogs.info('[Allbirds pixel] - Dynamic Yield Context - No context to restore');
    return true;
  }

  try {
    // Restore original context
    window.DY.recommendationContext = { ...originalContext };
    isContextSwitched = false;

    DataDogLogs.info('[Allbirds pixel] - Dynamic Yield Context - Restored original context', {
      restoredContext: originalContext,
    });

    // Clear saved context
    originalContext = null;
    return true;
  } catch (error) {
    DataDogLogs.error('[Allbirds pixel] - Dynamic Yield Context - Error restoring context', error);
    return false;
  }
};

/**
 * Gets the current context state
 * @returns {Object} Current context information
 */
export const getCurrentContextInfo = () => {
  return {
    currentContext: window.DY?.recommendationContext || null,
    isContextSwitched,
    isDynamicYieldReady: checkDynamicYieldReady(),
    originalContext,
  };
};

/**
 * Updates the product recommendation context with a new SKU
 * @param {string} sku - The new product SKU to set in the context
 * @returns {boolean} True if context was successfully updated
 */
export const updateProductRecommendationContext = (sku) => {
  if (!checkDynamicYieldReady()) {
    DataDogLogs.warn(
      '[Allbirds pixel] - Dynamic Yield Context - Cannot update context, DY not ready',
    );
    return false;
  }

  if (!sku) {
    DataDogLogs.warn(
      '[Allbirds pixel] - Dynamic Yield Context - Cannot update context, no SKU provided',
    );
    return false;
  }

  try {
    // Only update if we're in PRODUCT context and not switched to CART
    if (window.DY.recommendationContext?.type === 'PRODUCT' && !isContextSwitched) {
      window.DY.recommendationContext.data = [sku];
      DataDogLogs.info('[Allbirds pixel] - Dynamic Yield Context - Updated product context', {
        context: window.DY.recommendationContext,
        newSku: sku,
      });
      return true;
    } else {
      DataDogLogs.info('[Allbirds pixel] - Dynamic Yield Context - Skipped context update', {
        contextType: window.DY.recommendationContext?.type,
        isContextSwitched,
        sku,
      });
      return false;
    }
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds pixel] - Dynamic Yield Context - Error updating product context',
      error,
    );
    return false;
  }
};

/**
 * Updates the cart context with fresh SKUs if we're already in CART context
 * @param {string[]} cartSkus - Array of SKUs currently in the cart
 * @returns {boolean} True if context was successfully updated
 */
export const updateCartContext = (cartSkus = []) => {
  if (!checkDynamicYieldReady()) {
    DataDogLogs.warn(
      '[Allbirds pixel] - Dynamic Yield Context - Cannot update cart context, DY not ready',
    );
    return false;
  }

  // Only update if we're already in CART context
  if (!isContextSwitched || window.DY.recommendationContext?.type !== 'CART') {
    DataDogLogs.info(
      '[Allbirds pixel] - Dynamic Yield Context - Not in cart context, skipping update',
    );
    return false;
  }

  try {
    // Update the cart context with fresh SKUs
    window.DY.recommendationContext.data = cartSkus;

    DataDogLogs.info('[Allbirds pixel] - Dynamic Yield Context - Updated CART context', {
      cartSkus,
    });
    return true;
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds pixel] - Dynamic Yield Context - Error updating cart context',
      error,
    );
    return false;
  }
};
